import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { X, FileText } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { ChatAttachment, VoiceRecorder } from "../components/ChatAttachment";
import { PreferencesDiffCard } from "../components/PreferencesDiffCard";
import { bumpUnreadMessages } from "../lib/unreadMessages";
import { BRAND_COLORS } from "../lib/bookingStatus";
import { deDateTime } from "../lib/dateFormat";

const MAX_ATTACHMENTS = 3;
const ACCEPT = "image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt";

export default function CustomerChat() {
  const { user } = useAuth();
  const userId = user?.id;
  const { t, lang } = useI18n();
  const de = lang === "de";
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState([]); // [{ id, name, preview_url, is_image }]
  const fileInputRef = useRef(null);
  // Feb 2026 · User-Directive (1 Sep 2026) · „Neueste Nachrichten oben":
  // `topRef` sitzt über dem umgekehrt gerenderten Verlauf und dient beim
  // Öffnen des Threads sowie bei jeder neuen Nachricht als Scroll-Anker.
  // Ersetzt den früheren `endRef` (Auto-Scroll nach unten) komplett.
  const topRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/messages/${userId}?mark_read=true`);
    setMessages(data);
    // Feb 2026 · Badge SOFORT auf 0 nach Öffnen/Poll — Custom-Event feuert
    // Refresh in Layout-Header + Dashboard-Kachel (kein 12-s-Warten).
    bumpUnreadMessages();
  }, [userId]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [load]);

  // Feb 2026 · Neueste-oben-Regel: Jede Zustandsänderung an `messages.length`
  // (Erst-Load, Poll, eigenes Send) scrollt die Liste zurück nach oben,
  // damit die neueste Nachricht sofort im Blick ist.
  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [messages.length]);

  // Feb 2026 · Neueste-oben-Regel: Backend liefert Nachrichten ältest-zuerst;
  // wir kehren nur für die Darstellung um. Der interne Array-State bleibt
  // chronologisch aufsteigend, damit Send-Callbacks (Prepend/Append) und
  // Poll-Merges einfach bleiben.
  const orderedMessages = useMemo(() => [...(messages || [])].reverse(), [messages]);

  const pickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (files.length === 0) return;
    const free = MAX_ATTACHMENTS - pending.length;
    if (free <= 0) { toast.error(de ? `Maximal ${MAX_ATTACHMENTS} Anhänge pro Nachricht.` : `Maximum ${MAX_ATTACHMENTS} attachments per message.`); return; }
    const toUpload = files.slice(0, free);
    if (files.length > free) toast.info(de ? `Nur ${free} weitere Datei(en) werden hochgeladen (max. ${MAX_ATTACHMENTS} pro Nachricht).` : `Only ${free} more file(s) will be uploaded (max ${MAX_ATTACHMENTS} per message).`);
    setUploading(true);
    try {
      for (const file of toUpload) {
        const isImage = file.type.startsWith("image/");
        const localPreview = isImage ? URL.createObjectURL(file) : null;
        try {
          const fd = new FormData();
          fd.append("file", file);
          const { data: fileRef } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setPending((p) => [...p, { id: fileRef.id, name: file.name, preview_url: localPreview, is_image: isImage }]);
        } catch (err) {
          if (localPreview) URL.revokeObjectURL(localPreview);
          toast.error(`${file.name}: ${formatErr(err.response?.data?.detail) || err.message}`);
        }
      }
    } finally { setUploading(false); }
  };

  const removePending = (id) => {
    setPending((p) => {
      const target = p.find((x) => x.id === id);
      if (target?.preview_url) URL.revokeObjectURL(target.preview_url);
      return p.filter((x) => x.id !== id);
    });
  };

  const send = async (e) => {
    e?.preventDefault();
    const trimmed = text.trim();
    if (!trimmed && pending.length === 0) return;
    setSending(true);
    try {
      const payload = {};
      if (trimmed) payload.text = trimmed;
      if (pending.length > 0) payload.attachment_ids = pending.map((p) => p.id);
      const { data: msg } = await api.post("/messages", payload);
      setText("");
      pending.forEach((p) => { if (p.preview_url) URL.revokeObjectURL(p.preview_url); });
      setPending([]);
      setMessages((m) => [...m, msg]);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setSending(false); }
  };

  const canAddMore = pending.length < MAX_ATTACHMENTS;

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="customer-chat">
      <div className="mb-8">
        <div className="label-caps mb-3">{t.chat.team}</div>
        <h1 className="font-display text-4xl md:text-5xl text-navy">{t.chat.title}</h1>
        <p className="text-[15px] text-navy-dark/70 mt-3">{t.chat.lead}</p>
      </div>

      <div className="bg-white border border-navy/10 flex flex-col h-[65vh]">
        <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-4" data-testid="chat-message-list">
          <div ref={topRef} data-testid="chat-message-list-top" />
          {messages.length === 0 && (
            <div className="text-[13px] text-navy-dark/50 text-center py-16">{t.chat.empty}</div>
          )}
          {orderedMessages.map((m) => {
            const mine = m.sender_id === user.id;
            const isSystem = m.sender_role === "system";
            const isFamily = m.sender_role === "family";
            const atts = m.attachment_ids && m.attachment_ids.length ? m.attachment_ids : (m.attachment_id ? [m.attachment_id] : []);
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className="max-w-[70%]">
                  <div className={`label-caps mb-1 flex items-center gap-1.5 ${mine ? "justify-end" : ""}`}>
                    <span>{mine ? t.chat.you : (m.sender_name || t.chat.team)}</span>
                    {isFamily && !mine && (
                      <span
                        className="text-[9px] tracking-[0.15em] uppercase px-1.5 py-0.5 border"
                        style={{ background: BRAND_COLORS.ivoryDark || "#EDE3C8", color: BRAND_COLORS.goldDark || "#8A7635", borderColor: BRAND_COLORS.gold }}
                        data-testid={`chat-family-badge-${m.id}`}
                      >
                        {de ? "Familie" : "Family"}
                      </span>
                    )}
                    {mine && user.role === "family" && (
                      <span
                        className="text-[9px] tracking-[0.15em] uppercase px-1.5 py-0.5 border"
                        style={{ background: BRAND_COLORS.ivoryDark || "#EDE3C8", color: BRAND_COLORS.goldDark || "#8A7635", borderColor: BRAND_COLORS.gold }}
                        data-testid={`chat-family-badge-mine-${m.id}`}
                      >
                        {de ? "Familie" : "Family"}
                      </span>
                    )}
                  </div>
                  {isSystem ? (
                    m.meta?.kind === "preferences_diff" ? (
                      <div data-testid={`chat-system-prefs-diff-${m.id}`}>
                        <PreferencesDiffCard
                          diff={m.meta.diff}
                          snapshot={m.meta.snapshot}
                          testidBase={`chat-prefs-diff-${m.id}`}
                          label={de ? "Präferenzen aktualisiert" : "Preferences updated"}
                        />
                      </div>
                    ) : (
                      <div
                        className="px-4 py-2.5 border text-[12.5px] text-navy-dark/85 italic leading-relaxed"
                        style={{ background: "#F5EEDB", borderColor: BRAND_COLORS.gold }}
                        data-testid={`chat-system-msg-${m.id}`}
                      >
                        {m.text}
                      </div>
                    )
                  ) : (
                    <div
                      className={`px-4 py-3 border ${mine ? "bg-navy text-ivory border-navy" : "bg-white text-navy-dark border-ivory-dark"}`}
                      style={mine ? { background: BRAND_COLORS.navy } : {}}
                    >
                      {m.text && <div className="text-[14px] leading-relaxed whitespace-pre-wrap">{m.text}</div>}
                      {atts.length > 0 && (
                        <div className={`${m.text ? "mt-2" : ""} space-y-2`}>
                          {atts.map((fid) => (
                            <ChatAttachment key={fid} fileId={fid} mine={mine} transcription={m.voice_transcription} />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div className={`text-[10px] text-navy-dark/40 mt-1 ${mine ? "text-right" : ""}`}>
                    {deDateTime(m.created_at)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pending attachments preview grid */}
        {pending.length > 0 && (
          <div className="border-t border-navy/10 px-4 py-3 bg-ivory/60" data-testid="pending-attachments">
            <div className="flex items-center justify-between mb-2">
              <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>
                {pending.length} {de ? "von" : "of"} {MAX_ATTACHMENTS} {de ? "bereit" : "ready"}
              </div>
              <div className="text-[11px] text-navy-dark/50 italic">
                {de ? "Wird mit „Senden“ abgeschickt." : "Sent when you press \u201CSend\u201D."}
              </div>
            </div>
            <div className="flex gap-3 flex-wrap">
              {pending.map((p) => (
                <div key={p.id} className="relative border border-navy/15 bg-white" data-testid={`pending-item-${p.id}`}>
                  {p.is_image ? (
                    <img src={p.preview_url} alt={p.name} className="w-20 h-20 object-cover" />
                  ) : (
                    <div className="w-20 h-20 flex flex-col items-center justify-center gap-1 px-2 text-center">
                      <FileText className="w-6 h-6" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.4} />
                      <div className="text-[9px] text-navy-dark/70 leading-tight truncate w-full" title={p.name}>{p.name}</div>
                    </div>
                  )}
                  <button
                    type="button"
                    data-testid={`pending-remove-${p.id}`}
                    onClick={() => removePending(p.id)}
                    className="absolute -top-2 -right-2 bg-white text-navy border border-navy/30 hover:border-navy p-0.5 shadow-sm"
                    aria-label={de ? "Anhang entfernen" : "Remove attachment"}
                  >
                    <X className="w-3.5 h-3.5" strokeWidth={2} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={send} className="border-t border-navy/10 p-3 md:p-4 flex flex-wrap md:flex-nowrap items-center gap-2 md:gap-3">
          <input
            ref={fileInputRef}
            data-testid="chat-file-input"
            type="file"
            accept={ACCEPT}
            multiple
            onChange={pickFiles}
            className="hidden"
          />
          <input
            data-testid="chat-text-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={pending.length > 0 ? (de ? "Nachricht zum Anhang (optional) …" : "Message for the attachment (optional) …") : t.chat.placeholder}
            className="order-1 md:order-2 w-full md:w-auto md:flex-1 min-w-0 bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-2.5 text-navy-dark"
          />
          <div className="order-2 md:order-1 flex items-center gap-2 md:gap-3 shrink-0">
            <button
              type="button"
              data-testid="chat-attach-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || sending || !canAddMore}
              title={!canAddMore ? (de ? `Maximal ${MAX_ATTACHMENTS} Anhänge` : `Max ${MAX_ATTACHMENTS} attachments`) : ""}
              className="border border-navy/20 text-navy px-3 py-2.5 text-[12px] tracking-wider uppercase hover:border-gold transition-colors disabled:opacity-50 shrink-0"
            >
              {uploading ? "…" : t.chat.attach}
            </button>
            <VoiceRecorder onSent={(msg) => setMessages((m) => [...m, msg])} />
          </div>
          <button
            type="submit"
            data-testid="chat-send-btn"
            disabled={sending || (!text.trim() && pending.length === 0)}
            className="order-3 ml-auto md:ml-0 shrink-0 bg-navy text-ivory px-6 py-2.5 text-[12px] tracking-wider uppercase hover:opacity-90 transition-opacity disabled:opacity-40"
            style={{ background: BRAND_COLORS.navy }}
          >
            {sending ? "…" : t.chat.send}
          </button>
        </form>
      </div>
    </div>
  );
}
